import { useMemo, useState } from 'react'
import type { FramingContrast, Run, Valence } from '@/lib/types'

interface BrandAttr {
  attribute: string
  count: number
  valence: Valence
  rivals: string[]
}

function brandPortrait(framing: FramingContrast[], brand: string): BrandAttr[] {
  const brandLc = brand.toLowerCase()
  const mine = new Map<string, BrandAttr>()
  const rivalsByAttr = new Map<string, Set<string>>()

  for (const f of framing) {
    const key = f.attribute.toLowerCase()

    if (f.brand.toLowerCase() === brandLc) {
      const existing = mine.get(key)
      if (!existing || f.count > existing.count) {
        mine.set(key, {
          attribute: f.attribute,
          count: f.count,
          valence: f.valence,
          rivals: [],
        })
      }
    } else {
      const s = rivalsByAttr.get(key) ?? new Set<string>()
      s.add(f.brand)
      rivalsByAttr.set(key, s)
    }
  }

  const out: BrandAttr[] = []
  for (const [key, a] of mine) {
    out.push({ ...a, rivals: [...(rivalsByAttr.get(key) ?? [])] })
  }

  return out.sort(
    (a, b) => b.count - a.count || a.attribute.localeCompare(b.attribute)
  )
}

type Filter = 'all' | Valence

const FILTERS: { id: Filter; label: string }[] = [
  { id: 'all', label: 'All' },
  { id: 'positive', label: 'Positive' },
  { id: 'negative', label: 'Negative' },
  { id: 'neutral', label: 'Neutral' },
]

const VALENCE_DOT: Record<Valence, string> = {
  positive: 'bg-emerald-500',
  neutral: 'bg-muted-foreground/40',
  negative: 'bg-red-500',
}

const VALENCE_CHIP: Record<Valence, string> = {
  positive: 'border-emerald-500/30 bg-emerald-500/5',
  neutral: 'border-border bg-muted/30',
  negative: 'border-red-500/30 bg-red-500/5',
}

// One attribute chip. Fixed height so solo + shared chips line up. When
// showCompetitors is on and rivals exist, the first few names render inline and
// the rest go in a hover tooltip ("+N").
function Chip({
  attr,
  showCompetitors,
}: {
  attr: BrandAttr
  showCompetitors: boolean
}) {
  const shownRivals = attr.rivals.slice(0, 2)
  const extra = attr.rivals.length - shownRivals.length
  const tooltip =
    attr.rivals.length > 0
      ? `also said of ${attr.rivals.join(', ')}`
      : 'only said of you'

  return (
    <span
      title={tooltip}
      className={`inline-flex h-8 items-center gap-1.5 rounded-full border px-3 text-sm ${VALENCE_CHIP[attr.valence]}`}
    >
      <span
        className={`size-1.5 shrink-0 rounded-full ${VALENCE_DOT[attr.valence]}`}
      />
      <span className="capitalize">{attr.attribute}</span>
      <span className="text-[11px] text-muted-foreground tabular-nums">
        {attr.count}
      </span>
      {showCompetitors && attr.rivals.length > 0 && (
        <span className="text-xs text-muted-foreground">
          · vs {shownRivals.join(', ')}
          {extra > 0 && ` +${extra}`}
        </span>
      )}
    </span>
  )
}

const PREVIEW = 6

// FramingStrip is the condensed "how do the engines describe you?" section. The
// old standalone Framing lens is decomposed across the two tabs by variant:
//   - "solo"   (on the {brand}/Spaces tab): attributes ONLY said of the brand —
//     its unique portrait.
//   - "shared" (on the Rivals tab): attributes the brand SHARES with rivals —
//     the competitive-contrast portrait.
export function FramingStrip({
  run,
  variant,
}: {
  run: Run
  variant: 'solo' | 'shared'
}) {
  // This strip shows ONE group per tab: solo (unique to the brand) or shared
  // (also said of rivals). The split is the whole point — no duplication.
  const group = useMemo(() => {
    const all = brandPortrait(run.framing ?? [], run.brand)
    return variant === 'solo'
      ? all.filter((a) => a.rivals.length === 0)
      : all.filter((a) => a.rivals.length > 0)
  }, [run.framing, run.brand, variant])

  const [filter, setFilter] = useState<Filter>('all')
  const [showCompetitors, setShowCompetitors] = useState(variant === 'shared')
  const [expanded, setExpanded] = useState(false)

  const counts = useMemo(
    () => ({
      all: group.length,
      positive: group.filter((a) => a.valence === 'positive').length,
      negative: group.filter((a) => a.valence === 'negative').length,
      neutral: group.filter((a) => a.valence === 'neutral').length,
    }),
    [group]
  )

  if (group.length === 0) return null

  const filtered =
    filter === 'all' ? group : group.filter((a) => a.valence === filter)
  const shown = expanded ? filtered : filtered.slice(0, PREVIEW)
  const hidden = filtered.length - shown.length

  const heading =
    variant === 'solo'
      ? `How the engines uniquely describe ${run.brand}`
      : `How ${run.brand} is framed vs rivals`

  return (
    <section className="mb-6 space-y-3 rounded-xl border border-border bg-muted/20 p-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h3 className="text-sm font-semibold">{heading}</h3>

        <div className="flex items-center gap-3">
          {/* Show-competitors toggle — only meaningful on the shared variant. */}
          {variant === 'shared' && (
            <label className="flex cursor-pointer items-center gap-1.5 text-xs text-muted-foreground">
              <input
                type="checkbox"
                checked={showCompetitors}
                onChange={(e) => setShowCompetitors(e.target.checked)}
                className="size-3.5 accent-primary"
              />
              Show rivals
            </label>
          )}

          {/* Valence filter as an inline dropdown. */}
          <select
            value={filter}
            onChange={(e) => {
              const v = e.target.value
              if (
                v === 'all' ||
                v === 'positive' ||
                v === 'negative' ||
                v === 'neutral'
              ) {
                setFilter(v)
              }
            }}
            className="cursor-pointer rounded-md border border-border bg-card px-2 py-1 text-xs"
          >
            {FILTERS.map((f) => (
              <option key={f.id} value={f.id}>
                {f.label} ({counts[f.id]})
              </option>
            ))}
          </select>
        </div>
      </div>

      {shown.length === 0 ? (
        <p className="py-2 text-sm text-muted-foreground">
          No {filter} attributes.
        </p>
      ) : (
        <div className="flex flex-wrap gap-2">
          {shown.map((a) => (
            <Chip
              key={a.attribute}
              attr={a}
              showCompetitors={variant === 'shared' && showCompetitors}
            />
          ))}
          {hidden > 0 && (
            <button
              type="button"
              onClick={() => setExpanded(true)}
              className="inline-flex h-8 cursor-pointer items-center rounded-full border border-dashed border-border px-3 text-xs text-primary hover:bg-muted/40"
            >
              show {hidden} more
            </button>
          )}
          {expanded && filtered.length > PREVIEW && (
            <button
              type="button"
              onClick={() => setExpanded(false)}
              className="inline-flex h-8 cursor-pointer items-center rounded-full px-2 text-xs text-muted-foreground hover:underline"
            >
              show fewer
            </button>
          )}
        </div>
      )}
    </section>
  )
}
